<template>
  <ReadOnlyItem v-if="disabled" :gray="gray" :label="title" :value="inputTxt" />
  <IonItem
    v-else
    class="inline-number-input-item"
    :color="gray ? 'whitesmoke' : 'white'"
    lines="full"
    :id="`inlineInput-${title}`"
  >
    <div slot="start" class="flex items-center h-[60px] truncate">
      <ValidationIcon :valid-icon="icon" :state="state" :errors="errors"></ValidationIcon>
      <div class="font-medium text-gray-500">{{ title }}</div>
    </div>
    <div class="flex items-center h-[60px] w-full ml-auto">
      <input
        ref="inputRef"
        @blur="$emit('inputBlur')"
        type="number"
        :value="inputTxt"
        @input="onInput($event)"
        class="appearance-none text-right w-full bg-transparent outline-none placeholder:text-gray-500 placeholder:font-medium text-gray-700 font-medium"
        :placeholder="placeholder || `Enter ${title}`"
      />
      <IconButton
        v-if="inputTxt && clearable"
        class="ml-2"
        size="small"
        icon="icon-x"
        @click="onClearClicked($event)"
        color="medium"
      ></IconButton>
    </div>
  </IonItem>
</template>

<script setup lang="ts">
import { IonItem } from '@ionic/vue';
import IconButton from '../IconButton.vue';
import { onMounted, ref, watch } from 'vue';
import { FormControlError, FormControlState } from '@/composables/use-form-control';
import ValidationIcon from '../ValidationIcon.vue';
import ReadOnlyItem from '../ReadOnlyItem.vue';

withDefaults(
  defineProps<{
    title: string;
    placeholder?: string;
    gray?: boolean;
    clearable?: boolean;
    icon?: string;
    state?: FormControlState;
    errors?: FormControlError[];
    disabled?: boolean;
  }>(),
  { clearable: true }
);

const inputRef = ref<InstanceType<typeof HTMLInputElement>>();
defineEmits<{ (e: 'inputBlur'): void }>();
const vModel = defineModel({ type: Number });
const inputTxt = ref<string>('');

watch(inputTxt, (txt) => {
  const parsed = txt ? +txt : undefined;
  if (parsed !== vModel.value) {
    vModel.value = parsed;
  }
});

watch(vModel, (num) => {
  const parsed = inputTxt.value ? +inputTxt.value : undefined;
  if (parsed !== num) {
    updateInputText(num?.toString() || '');
  }
});

onMounted(() => {
  const num = vModel.value;
  const txt = num || num === 0 ? num.toString() : '';
  if (txt !== inputTxt.value) {
    updateInputText(txt);
  }
});

function onInput(ev: Event) {
  // Step 1: Remove all non-numeric characters except the decimal
  let txt = (ev?.target as HTMLInputElement)?.value?.toString() || '';
  txt = txt.replace(/[^0-9.]/g, '');

  // Step 2: Ensure only one decimal point remains
  txt = txt.replace(/\.{2,}/g, '.'); // Replace consecutive dots with a single dot
  txt = txt.replace(/(\..*?)\./g, '$1'); // Keep the first decimal and remove any subsequent ones

  updateInputText(txt);
}

function updateInputText(txt: string) {
  const oldTxt = inputTxt.value;
  if (inputRef.value) {
    if (txt || oldTxt.length === 1) {
      inputTxt.value = txt;
    } else {
      inputTxt.value = oldTxt;
      inputRef.value.value = oldTxt;
    }
  } else {
    inputTxt.value = txt;
  }
}

function onClearClicked(ev: MouseEvent) {
  ev.stopPropagation();
  ev.preventDefault();
  vModel.value = undefined;
  inputTxt.value = '';
}
</script>

<style scoped>
ion-item.inline-number-input-item::part(native) {
  --border-width: 1px;
}

ion-item.inline-number-input-item {
  --padding-start: 0px;
}

/* Hide up/down arrows for Chrome and Safari */
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* And for Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}
</style>
